// AdminPanel/ApiKeyFinder — the Search tab's "API Key" lookup: paste a
// customer's full API key, resolve which org/tenant owns it, jump to that
// org's admin details dialog.
//
// Backed by OnboardingAdminService.AdminSearchApiKey
// (POST /api/v1/admin/search/api_key) — the server matches by SHA256 hash
// (key_hash / short_key_hash), so only the COMPLETE key resolves; a prefix
// or fragment hashes to something else and returns found=false. Revoked
// keys still resolve (isActive=false) so support can explain why a key
// stopped working.
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Search, VpnKey} from '@mui/icons-material';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, TextField, Typography
} from '@mui/material';

import {adminSearchApiKey} from '../../actions/adminPanel';

class ApiKeyFinder extends Component {
  state = {
    query: '',
    searching: false,
    result: null,      // {found, tenantId, key, org} after a completed search
    error: null
  };

  // Guards against a stale response landing after a newer search (or
  // unmount) — only the latest request may write state.
  searchSeq = 0;

  componentWillUnmount() {
    this.searchSeq++;
  }

  search = async () => {
    const query = this.state.query.trim();
    if (!query) {
      this.setState({error: 'Paste the full API key to search.'});
      return;
    }

    const seq = ++this.searchSeq;
    this.setState({searching: true, result: null, error: null});
    try {
      const resp = await adminSearchApiKey(query);
      if (this.searchSeq !== seq) return;
      const data = resp?.data || {};
      const tenantId = data.tenantId ?? data.tenant_id;
      // Join to the loaded customer list so "Open Org Details" gets the
      // same row shape the Customers table passes to the details dialog.
      // Orgs missing from the list (still loading / filtered out) fall
      // back to a minimal row — the dialog fetches full details itself.
      const org = data.found
        ? this.props.orgs?.find((o) => o.instituteId === tenantId) ||
          {instituteId: tenantId, id: tenantId, name: tenantId}
        : null;
      this.setState({
        searching: false,
        result: {found: !!data.found, tenantId, key: data.key || null, org}
      });
    } catch (err) {
      if (this.searchSeq !== seq) return;
      const status = err?.response?.status;
      const msg =
        status === 403 ? 'You don\'t have permission to use admin search.' :
          err?.response?.data?.message || err?.message || 'Search failed — please try again.';
      this.setState({searching: false, error: msg});
    }
  };

  renderResult() {
    const {result} = this.state;
    if (!result) return null;
    if (!result.found) {
      return (
        <Alert severity="info">
          No org found for this API key. Make sure the <strong>complete</strong> key was
          pasted — the lookup matches the full key, not a prefix.
        </Alert>
      );
    }

    const {org, key} = result;
    const contactName = org.contactName || org.onboarding?.contactName;
    const isActive = key ? (key.isActive ?? key.is_active) : undefined;
    const createdAt = key ? (key.createdAt ?? key.created_at) : null;
    return (
      <Card sx={{border: '1px solid #c8e6c9'}}>
        <CardContent>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap'}}>
            <Box sx={{minWidth: 0}}>
              <Typography variant="h6">{org.name || contactName || result.tenantId}</Typography>
              {contactName && (
                <Typography variant="body2" color="text.secondary">{contactName}</Typography>
              )}
              <Typography variant="caption" color="text.secondary" component="div">
                Tenant: <code>{result.tenantId}</code>
              </Typography>
            </Box>
            <Button variant="contained" onClick={() => this.props.onOpenOrg(org)} sx={{flexShrink: 0}}>
              Open Org Details
            </Button>
          </Box>
          {key && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap'}}>
              <VpnKey sx={{fontSize: 16, color: '#888'}} />
              <code style={{fontSize: 12}}>{key.keyPrefix ?? key.key_prefix ?? '-'}</code>
              <Typography variant="caption">{key.keyName ?? key.key_name}</Typography>
              {isActive
                ? <Chip label="Active" size="small" color="success" />
                : <Chip label="Revoked" size="small" color="error" variant="outlined" />}
              {createdAt && (
                <Typography variant="caption" color="text.secondary">
                  created {new Date(createdAt).toLocaleString()}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  render() {
    const {query, searching, error} = this.state;

    return (
      <div>
        <Card sx={{mb: 2}}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              Paste a customer&apos;s <strong>full</strong> API key to find which org it
              belongs to. Revoked keys are found too — useful when a customer asks why
              their key stopped working.
            </Typography>
            <Box sx={{display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap'}}>
              <TextField
                label="API Key"
                value={query}
                onChange={(e) => this.setState({query: e.target.value, error: null})}
                onKeyDown={(e) => { if (e.key === 'Enter' && !searching) this.search(); }}
                size="small"
                sx={{flex: 1, minWidth: 320}}
                inputProps={{style: {fontFamily: 'monospace'}}}
                placeholder="Paste the complete API key"
                disabled={searching}
                autoFocus
              />
              <Button
                variant="contained"
                startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <Search />}
                onClick={this.search}
                disabled={searching}
              >
                {searching ? 'Searching…' : 'Search'}
              </Button>
            </Box>
            {error && <Alert severity="warning" sx={{mt: 2}}>{error}</Alert>}
          </CardContent>
        </Card>

        {this.renderResult()}
      </div>
    );
  }
}

ApiKeyFinder.propTypes = {
  orgs: PropTypes.array,
  onOpenOrg: PropTypes.func.isRequired
};

export default ApiKeyFinder;
